
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

const TreatmentsMegaMenu = () => {
  const [isOpen, setIsOpen] = useState(false);

  const treatmentCategories = [
    {
      title: "Injectables",
      description: "Botox, fillers & neurotoxins",
      icon: "💉",
      items: [
        { name: "Botox", href: "/treatments/botox", price: "Starting at $12/unit" },
        { name: "Lip Filler", href: "/treatments/lip-filler", price: "Starting at $599" },
        { name: "Kybella", href: "/treatments/kybella", price: "Starting at $800" }
      ]
    },
    {
      title: "Skin Treatments",
      description: "Facials, lasers & peels",
      icon: "✨",
      items: [
        { name: "RF Microneedling", href: "/treatments/rf-microneedling", price: "Starting at $299" },
        { name: "Laser Facial", href: "/treatments/laser-facial", price: "Starting at $199" },
        { name: "VI Peel", href: "/treatments/vi-peel", price: "Starting at $250" }
      ]
    },
    {
      title: "Wellness",
      description: "IV therapy & body treatments",
      icon: "💧",
      items: [
        { name: "IV Therapy", href: "/treatments/iv-therapy", price: "Starting at $150" },
        { name: "Skin Boosters", href: "/treatments/skin-boosters", price: "Starting at $299" },
        { name: "PRF", href: "/treatments/prf", price: "Starting at $199" }
      ]
    }
  ];

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button className="flex items-center space-x-1 text-juv-deep-chestnut hover:text-juv-terracotta transition-colors font-light tracking-wide py-2">
        <span>Treatments</span>
        <ChevronDown size={16} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute top-full left-0 mt-2 w-[700px] -translate-x-1/4 z-50"
          >
            <div className="bg-white/95 backdrop-blur-md border border-juv-rose-clay/20 rounded-2xl shadow-luxury overflow-hidden">
              <div className="bg-gradient-to-r from-juv-soft-blush/10 to-juv-rose-clay/10 border-b border-juv-rose-clay/20 p-6">
                <h3 className="text-xl font-bold text-juv-deep-chestnut">Our Treatments</h3>
                <p className="text-juv-deep-chestnut/80 text-sm mt-1">Choose your path to transformation</p>
              </div>

              <div className="grid grid-cols-3 gap-6 p-6">
                {treatmentCategories.map((category, index) => (
                  <motion.div
                    key={category.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.3 }}
                    className="group"
                  >
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-juv-rose-clay-light/30 to-juv-rose-clay/30 rounded-2xl flex items-center justify-center text-2xl group-hover:scale-110 transition-transform duration-300 mx-auto mb-4">
                        {category.icon}
                      </div>
                      
                      <h4 className="font-semibold text-juv-deep-chestnut group-hover:text-juv-terracotta transition-colors mb-2">
                        {category.title}
                      </h4>
                      <p className="text-sm text-juv-deep-chestnut/80 mb-4">
                        {category.description}
                      </p>
                      
                      <div className="space-y-2">
                        {category.items.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            className="block p-2 rounded-lg hover:bg-juv-soft-blush/30 transition-colors group/item"
                          >
                            <div className="text-sm font-medium text-juv-deep-chestnut group-hover/item:text-juv-terracotta">
                              {item.name}
                            </div>
                            <div className="text-xs text-juv-terracotta">
                              {item.price}
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-juv-soft-blush to-juv-rose-clay/10 border-t border-juv-rose-clay/20 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-juv-deep-chestnut">Not sure what's right for you?</h4>
                    <p className="text-sm text-juv-deep-chestnut/80">Take our personalized quiz to find your perfect treatment path</p>
                  </div>
                  <Link
                    to="/quiz"
                    className="bg-gradient-to-r from-juv-rose-clay to-juv-terracotta text-white px-6 py-3 rounded-full hover:shadow-glow transition-all duration-300 hover:scale-105 font-medium"
                  >
                    Take Quiz
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TreatmentsMegaMenu;
